import Vue from 'vue';
import { DirectDebitFile } from '@accounting/interfaces/direct-debit-file';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import Input from '@shared/components/forms/inputs/input/input.vue';
import RadioGroup from '@shared/components/forms/inputs/radio-group/radio-group.vue';
import Textarea from '@shared/components/forms/inputs/textarea/textarea.vue';

@Component({
  name: 'bpc-ticket-to-direct-debit-file-modal',
  components: {
    'bpc-input': Input,
    'bpc-radio-group': RadioGroup,
    'bc-textarea': Textarea,
  },
})
export default class TicketToDirectDebitFileModal extends Vue {
  // PROPS
  @Prop({ type: String, default: 'ticket-direct-debit-file-modal' })
  readonly modalId: string;
  @Prop({ type: Boolean, default: false }) readonly showModal: boolean;
  @Prop({
    type: Array,
    default() {
      return [];
    },
  })
  readonly selectedItemIds: number[];
  @Prop({
    type: String,
    default: 'global.generic.error_form_action',
  })
  readonly errorMessageText: string;
  @Prop({ type: Boolean, default: false }) readonly withComment: boolean;

  loading: boolean = false;
  selectedDirectDebitFiles: DirectDebitFile = null;
  directDebitFiles: DirectDebitFile[] = [];
  requestSent: boolean = false;
  isProcessing: boolean = false;
  eventsSuccess = [];
  showErrors = false;
  errorsToShow = [];
  comment: string = '';

  //par défaut : date du jour
  titleNewDirectDebitFile: string = `${this.$t(
    'accounting.direct_debit.label',
  )} ${this.$moment().format('DD/MM/YYYY')}`;

  get tenantId(): number {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  // LIFECYCLE
  mounted() {
    this.showErrors = false;
    if (this.showModal) {
      this.openModal();
    }
  }

  // METHODS
  closeModal() {
    this.showErrors = false;
    this.$emit('close-modal');
  }

  resetModal() {
    this.selectedDirectDebitFiles = null;
    this.eventsSuccess = [];
    this.requestSent = false;
    this.isProcessing = false;
    this.showErrors = false;
    this.titleNewDirectDebitFile = `${this.$t(
      'accounting.direct_debit.label',
    )} ${this.$moment().format('DD/MM/YYYY')}`;
  }

  openModal() {
    this.showErrors = false;
    this.$bvModal.show(this.modalId);
    this.getDirectDebitFiles();
  }

  onConfirm() {
    this.showErrors = false;
    const basePath = this.$bpcApiRouteHelper.getEntityPath('direct-debit-files');
    const sessionCode = this.getSessionCode();
    this.listenToWebsocketAddToDirectDebitFileChannel(sessionCode);

    this.requestSent = true;
    this.isProcessing = true;

    const payload: AnyEntity = {
      invoiceIds: this.selectedItemIds,
      session_code: sessionCode,
      direct_debit_file_id: this.selectedDirectDebitFiles,
      title_new_direct_debit_file: this.titleNewDirectDebitFile,
      comment: this.comment,
    };
    this.$bpcApi.post(basePath + '/add-to-direct-debit-file', payload).subscribe(
      res => {
        this.requestSent = true;
      },
      error => {
        this.requestSent = false;
        this.isProcessing = false;
        this.$bpcMessage.showErrorMessage({ text: this.errorMessageText });
      },
    );
  }

  getSessionCode() {
    return `add-to-direct-debit-file-${this.tenantId}-${this.generateUUIDv4()}`;
  }

  generateUUIDv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  listenToWebsocketAddToDirectDebitFileChannel(sessionCode) {
    this.isProcessing = true;
    this.$bpcEcho
      .getAddTicketToDirectDebitFileChannel(sessionCode)
      .listen('.BPC\\Accounting\\Events\\TicketsAddedToDirectDebitFile', async data => {
        this.eventsSuccess.push(data);
        this.$bpcEcho.leaveAddTicketToDirectDebitFileChannel(sessionCode);
        this.closeModal();
      })
      .listen('.BPC\\Accounting\\Events\\ErrorAddingTicketToDirectDebitFile', data => {
        this.showErrors = true;
        this.errorsToShow.push(this.$t(data.result, { ticketCode: data.ticketCode }));
        this.isProcessing = false;
        this.$bpcEcho.leaveAddTicketToDirectDebitFileChannel(sessionCode);
      })
      .listen('.BPC\\Accounting\\Events\\GlobalErrorTicketsAddedToDirectDebitFile', data => {
        this.$bpcMessage.showErrorMessage({
          text: data.result,
        });
        this.isProcessing = false;
        this.$bpcEcho.leaveAddTicketToDirectDebitFileChannel(sessionCode);
      });
  }

  async getDirectDebitFiles() {
    const params = { status: 'draft' };
    this.loading = true;
    const baseUrl = this.$bpcApiRouteHelper.getEntityPath('direct-debit-file');
    const result: BPCGenericApiResponse<DirectDebitFile[]> = await this.$bpcApi
      .get(`${baseUrl}`, { params: params })
      .toPromise();
    this.directDebitFiles = result.data;
    this.loading = false;
  }

  get selectOpenDirectDebitFileLabel() {
    return this.$t('global.choose_open_direct_debit_file.label');
  }

  get titleDirectDebitFileLabel() {
    return this.$t('direct_debit_file.title.label');
  }

  get invoiceCommentLabel() {
    return this.$t('invoice.comment.label');
  }

  get availableDirectDebitFiles() {
    const newfile = [{ text: `${this.$t('global.option.new_direct_file')}`, value: null }];
    const openFiles = this.directDebitFiles.map(directDebitFile => {
      return {
        text: directDebitFile.title,
        value: directDebitFile.id,
      };
    });
    return newfile.concat(openFiles);
  }

  get showTitleDirectDebitFile() {
    return this.selectedDirectDebitFiles ? false : true;
  }

  onSelectDirectDebitFile() {
    this.selectedDirectDebitFiles;
    this.$emit('select-direct-debit-file-clicked', this.selectedDirectDebitFiles);
  }

  // WATCHERS

  @Watch('showModal')
  onShowModalChange(val) {
    if (val) {
      this.resetModal();
      this.openModal();
    } else {
      this.$bvModal.hide(this.modalId);
    }
  }
}
